import { ShoppingCart } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/format";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

export function ProductCard({ product }) {
  const { addToCart, cartItems, loading, removeItem, updateQuantity } = useCart();
  const [imageError, setImageError] = useState(false);

  const uomOptions = product.dynamicUOM || [];
  const hasMultipleUoms = uomOptions.length > 1;
  const activeUom = hasMultipleUoms ? product.selectedUOM || uomOptions[0] : {
    uomId: 2,
    uomDesc: "UNIT",
    unitPerCarton: product.unitPerCarton || 1,
    price: product.price,
  };
  const displayPrice = Number(activeUom.price || product.price || 0);
  const productForCart = {
    ...product,
    price: displayPrice,
    uomId: activeUom.uomId,
    uomDesc: activeUom.uomDesc,
    unitPerCarton: activeUom.unitPerCarton || 1,
    selectedUOM: {
      uomId: activeUom.uomId,
      uomDesc: activeUom.uomDesc,
      unitPerCarton: activeUom.unitPerCarton || 1,
      price: displayPrice,
    },
  };
  const productIdKey = String(product.productId);
  const productUomId = Number(productForCart.uomId || 0);
  const cartItem = useMemo(
    () =>
      cartItems.find(
        (item) => {
          const itemProductId = String(item.productId ?? item.ProductID ?? item.ProductId ?? "");
          const itemUomId = Number(item.uomId ?? item.UOMID ?? 0);

          if (itemProductId !== productIdKey) {
            return false;
          }

          return !itemUomId || !productUomId || itemUomId === productUomId;
        }
      ) || null,
    [cartItems, productIdKey, productUomId]
  );

  async function handleAddToCart() {
    try {
      await addToCart(productForCart, 1);
    } catch (error) {
      // CartContext already captures the error for UI display.
    }
  }

  async function handleQuantityChange(nextQuantity) {
    if (!cartItem) {
      return;
    }

    try {
      if (nextQuantity <= 0) {
        await removeItem(cartItem);
        return;
      }

      await updateQuantity(cartItem, nextQuantity);
    } catch (error) {
      // CartContext already captures the error for UI display.
    }
  }

  return (
    <Card className="group overflow-hidden rounded-2xl border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:shadow-soft">
      <Link
        className="block"
        state={{ product }}
        to={`/products/${encodeURIComponent(product.productId)}`}
      >
        <div className="h-[118px] bg-secondary sm:h-[150px] lg:h-[160px] 2xl:h-[170px]">
          {product.imageUrl ? (
            !imageError ? (
            <img
              alt={product.productName}
              className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              loading="lazy"
              onError={() => setImageError(true)}
              src={product.imageUrl}
            />
            ) : (
              <div className="flex h-full items-center justify-center px-4 text-center text-xs font-semibold text-muted-foreground">
                No Image Available
              </div>
            )
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center text-xs font-semibold text-muted-foreground">
              No Image Available
            </div>
          )}
        </div>
      </Link>
      <CardContent className="p-2.5 sm:p-3.5">
        <div className="flex min-h-[74px] flex-col sm:min-h-[82px]">
          <Link
            className="block"
            state={{ product }}
            to={`/products/${encodeURIComponent(product.productId)}`}
          >
            <h3 className="line-clamp-2 font-display text-sm font-semibold leading-snug transition group-hover:text-primary sm:text-base">
              {product.productName}
            </h3>
          </Link>
          {product.productCode ? (
            <p className="mt-1.5 truncate text-xs text-muted-foreground">Code: {product.productCode}</p>
          ) : null}
          <div className="mt-auto flex flex-col gap-1 pt-2 sm:flex-row sm:items-end sm:justify-between sm:gap-2 sm:pt-3">
            <p className="text-base font-semibold text-primary sm:text-lg">{formatCurrency(displayPrice)}</p>
            <p className="text-[11px] font-semibold text-muted-foreground sm:pb-0.5 sm:text-xs">UOM: {activeUom.uomDesc || "UNIT"}</p>
          </div>
        </div>

        {cartItem ? (
          <div className="mt-2 flex h-9 w-full items-center justify-between rounded-xl border border-primary bg-primary/5 px-2 text-primary transition sm:mt-3 sm:h-10">
            <button
              className="rounded-lg px-3 py-1 text-base font-bold transition hover:bg-primary hover:text-primary-foreground"
              disabled={loading}
              onClick={() => handleQuantityChange(cartItem.qty - 1)}
              type="button"
            >
              -
            </button>
            <span className="min-w-8 text-center text-sm font-bold">{cartItem.qty}</span>
            <button
              className="rounded-lg px-3 py-1 text-base font-bold transition hover:bg-primary hover:text-primary-foreground"
              disabled={loading}
              onClick={() => handleQuantityChange(cartItem.qty + 1)}
              type="button"
            >
              +
            </button>
          </div>
        ) : (
          <Button
            className="mt-2 h-9 w-full rounded-xl text-xs transition sm:mt-3 sm:h-10 sm:text-sm"
            disabled={loading}
            onClick={handleAddToCart}
            type="button"
          >
            <ShoppingCart className="h-4 w-4" />
            + Add to Cart
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
